<template>
  <div class="d-flex">
    <!-- Fondo oscuro del menú en móvil -->
    <div v-if="mobileOpen" class="sidebar-backdrop" @click="closeMobile"></div>

    <!-- Sidebar -->
    <nav :class="['sidebar d-flex flex-column text-white', { collapsed: compact, open: mobileOpen }]"
      aria-label="Menú principal">
      <!-- Cerrar (solo en el cajón móvil) -->
      <div class="sidebar-mobile-header d-flex d-lg-none justify-content-end">
        <button class="sidebar-icon-btn" @click="closeMobile" aria-label="Cerrar menú">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Card saldo -->
      <div v-if="!compact && !hasRole(['helpdesk'])" class="saldo-card">
        <div class="saldo-label">
          <i class="bi bi-wallet2"></i>
          <span>Saldo disponible</span>
        </div>
        <div class="saldo-row">
          <div v-if="isLoadingBalance" class="ew-balance-skeleton"></div>
          <span v-else class="saldo-amount">{{ formatCurrency(total_monedero) }} <small>MXN</small></span>
          <router-link class="saldo-link" to="/prepaids" aria-label="Gestionar saldo"
            v-b-tooltip.hover.right.viewport="'Gestionar saldo'">
            <i class="bi bi-arrow-right-circle"></i>
          </router-link>
        </div>
      </div>

      <!-- Menú agrupado por secciones -->
      <div class="sidebar-menu" @scroll="hideTip">
        <div v-for="section in menuSections" :key="section.key" class="sidebar-section">
          <div v-if="section.label" class="sidebar-section-label">
            <span v-if="!compact">{{ section.label }}</span>
          </div>
          <ul class="nav flex-column">
            <li class="nav-item" v-for="item in section.items" :key="item.key">
              <router-link :to="item.to" class="sidebar-link" :class="{ 'active-link': activeItem === item.key }"
                :aria-label="compact ? item.label : null"
                v-b-tooltip.hover.right.viewport="compact ? '' : item.tooltip"
                @mouseenter.native="showTip($event, item)" @mouseleave.native="hideTip"
                @focus.native="showTip($event, item)" @blur.native="hideTip">
                <i :class="['bi', item.icon, 'sidebar-link-icon']"></i>
                <span v-if="!compact" class="sidebar-link-label">{{ item.label }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </div>

      <!-- Colapsar (escritorio) -->
      <div class="sidebar-footer d-none d-lg-block">
        <button class="sidebar-collapse-btn" @click="toggleSidebar"
          :aria-label="isCollapsed ? 'Expandir menú' : 'Colapsar menú'"
          @mouseenter="showTip($event, { label: isCollapsed ? 'Expandir menú' : 'Colapsar menú' }, true)"
          @mouseleave="hideTip">
          <i :class="isCollapsed ? 'bi bi-chevron-double-right' : 'bi bi-chevron-double-left'"></i>
        </button>
      </div>
    </nav>

    <!-- Nombre de la opción con el menú colapsado: aparece al instante -->
    <div v-if="tip.visible" class="sidebar-tip" role="tooltip" :style="{ top: tip.top + 'px', left: tip.left + 'px' }">
      {{ tip.text }}
    </div>

    <!-- Contenido principal -->
    <div class="flex-fill bg-light min-vh-100 d-flex flex-column">
      <slot></slot>
    </div>
    <LowBalanceModal :isVisible="showLowBalanceAlert" :currentBalance="total_monedero"
      @close="showLowBalanceAlert = false" />
  </div>
</template>

<script>
import LowBalanceModal from '@/components/LowBalanceModal.vue';
import axios from "axios";
import commons from "../commons";
import { roleMixin } from '@/mixins/roleMixin';

const COLLAPSED_KEY = 'ew-sidebar-collapsed';
const ALL_ROLES = ['client', 'superadmin', 'helpdesk', 'sales', 'user', 'admin', 'admin_add'];

const SECTIONS = [
  { key: 'general', label: null },
  { key: 'operacion', label: 'Operación' },
  { key: 'finanzas', label: 'Finanzas' },
  { key: 'configuracion', label: 'Configuración' },
];

export default {
  mixins: [roleMixin],
  components: {
    LowBalanceModal
  },
  data() {
    return {
      showLowBalanceAlert: false,
      isCollapsed: this.readCollapsed(),
      mobileOpen: false,
      isMobile: false,
      tip: { visible: false, text: '', top: 0, left: 0 },
      activeItem: 'dashboard',
      total_monedero: 0,
      isLoadingBalance: true,
      userRol: 'user',
      menuItems: [
        {
          label: "Dashboard", to: "/dashboard", icon: "bi-grid-1x2", key: "dashboard", section: 'general',
          tooltip: "Ver el resumen general de tu cuenta y actividad reciente",
          roles: ALL_ROLES
        },
        {
          label: "Cotizador", to: "/dashboard/shipment/new", icon: "bi-calculator", key: "shipments", section: 'operacion',
          tooltip: "Crear un nuevo envío o cotizar un envío rápido",
          roles: ['client', 'superadmin', 'sales', 'user', 'admin', 'admin_add']
        },
        {
          label: "Gestión de Envíos", to: "/shipments", icon: "bi-box-seam", key: "historial", section: 'operacion',
          tooltip: "Ver y administrar todos tus envíos realizados",
          roles: ['client', 'superadmin', 'sales', 'user', 'admin', 'admin_add']
        },
        {
          label: "Recolección", to: "/recoleccion", icon: "bi-truck", key: "recoleccion", section: 'operacion',
          tooltip: "Solicitar una recolección o ver el estado de tus recolecciones programadas",
          roles: ['client', 'superadmin', 'sales', 'user', 'admin', 'admin_add']
        },
        {
          label: "Cargos adicionales", to: "/overweight", icon: "bi-receipt-cutoff", key: "sobrepesos", section: 'operacion',
          tooltip: "Ver y gestionar los cargos adicionales de tus envíos",
          roles: ['client', 'superadmin', 'helpdesk', 'admin', 'admin_add']
        },
        {
          label: "Monedero", to: "/prepaids", match: ["/prepaid/"], icon: "bi-wallet2", key: "movimientos", section: 'finanzas',
          tooltip: "Gestionar el saldo de tu monedero y ver tus movimientos de crédito",
          roles: ['client', 'superadmin', 'admin', 'admin_add']
        },
        {
          label: "Reportes", to: "/Reports", icon: "bi-bar-chart-line", key: "reporte", section: 'finanzas',
          tooltip: "Generar reportes detallados de tus envíos y actividad",
          roles: ['client', 'superadmin', 'helpdesk', 'sales', 'admin', 'admin_add']
        },
        {
          label: "Catálogo de Direcciones", to: "/addresses", icon: "bi-geo-alt", key: "direcciones", section: 'configuracion',
          tooltip: "Gestionar tus direcciones guardadas para envíos frecuentes",
          roles: ALL_ROLES
        },
        {
          label: "Usuarios y Roles", to: "/users", icon: "bi-people", key: "usuarios", section: 'configuracion',
          tooltip: "Gestionar usuarios, roles y permisos de tu cuenta",
          roles: ['client', 'superadmin', 'sales', 'admin_add']
        },
        {
          label: "Clientes", to: "/clients", icon: "bi-buildings", key: "clients", section: 'configuracion',
          tooltip: "Gestionar clientes y cuentas asociadas",
          roles: ['superadmin']
        },
        {
          label: "Integraciones", to: "/integrations", icon: "bi-plug", key: "integraciones", section: 'configuracion',
          tooltip: "Configurar integraciones, APIs y conectores para automatizar tus envíos",
          roles: ['superadmin']
        },
        {
          label: "Notificaciones", to: "/notifications", icon: "bi-bell", key: "notificaciones", section: 'configuracion',
          tooltip: "Ver administradores con saldo bajo y enviarles una notificación",
          roles: ['superadmin']
        }
      ]
    };
  },
  watch: {
    '$route.path'(newPath) {
      this.updateActiveFromPath(newPath);
      this.closeMobile();
    }
  },
  created() {
    this.initRole();
    this.updateActiveFromPath(this.$route.path);
    this.$root.$on('ew-toggle-sidebar', this.toggleMobile);
    this.mobileQuery = window.matchMedia('(max-width: 991.98px)');
    this.onMobileChange();
    this.mobileQuery.addListener(this.onMobileChange);
    document.addEventListener('keydown', this.onKeydown);
    axios
      .get(`${commons.apiURL}/api/v1/users/${localStorage.getItem("ew-user-id")}`)
      .then((response) => {
        if (response.data) {
          localStorage.setItem('ew-user-role', response.data.rol);
          localStorage.setItem('ew-user-parentClient', response.data.parentClient);
          localStorage.setItem('ew-user-email', response.data.email);
          this.userRol = localStorage.getItem('ew-user-role');
          this.initRole();
        }
      });
    setTimeout(() => {
      this.getTotalAmountPrepaids();
    }, 1500);
  },
  beforeDestroy() {
    this.$root.$off('ew-toggle-sidebar', this.toggleMobile);
    this.mobileQuery.removeListener(this.onMobileChange);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('ew-sidebar-locked');
  },
  computed: {
    // En móvil el cajón siempre muestra los textos, aunque en escritorio esté colapsado
    compact() {
      return this.isCollapsed && !this.isMobile;
    },
    menuItemsVisible() {
      if (!this.userRol) {
        return [];
      }
      return this.menuItems.filter(item => item.roles.includes(this.userRol));
    },
    menuSections() {
      return SECTIONS
        .map(section => ({
          ...section,
          items: this.menuItemsVisible.filter(item => item.section === section.key)
        }))
        .filter(section => section.items.length > 0);
    }
  },
  methods: {
    // Gana la ruta más larga que coincida, para que /dashboard/shipment/new
    // marque "Cotizador" y no "Dashboard".
    updateActiveFromPath(path) {
      const currentPath = path.toLowerCase();
      let best = null;
      let bestLength = -1;
      for (const item of this.menuItems) {
        const prefixes = [item.to, ...(item.match || [])].map(p => p.toLowerCase());
        for (const prefix of prefixes) {
          const base = prefix.replace(/\/$/, '');
          const matches = currentPath === base || currentPath.startsWith(base + '/');
          if (matches && base.length > bestLength) {
            best = item.key;
            bestLength = base.length;
          }
        }
      }
      this.activeItem = best || '';
    },
    initRole() {
      this.userRol = localStorage.getItem('ew-user-role');
      if (!this.userRol) {
        this.userRol = 'user';
      }
    },
    readCollapsed() {
      try {
        return localStorage.getItem(COLLAPSED_KEY) === 'true';
      } catch (e) {
        return false;
      }
    },
    showTip(event, item, always = false) {
      if (!this.compact && !always) {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.tip = { visible: true, text: item.label, top: rect.top + rect.height / 2, left: rect.right + 10 };
    },
    hideTip() {
      this.tip.visible = false;
    },
    toggleSidebar() {
      this.hideTip();
      this.isCollapsed = !this.isCollapsed;
      try {
        localStorage.setItem(COLLAPSED_KEY, String(this.isCollapsed));
      } catch (e) {
        // sin almacenamiento disponible: solo se pierde la preferencia
      }
    },
    onMobileChange() {
      this.isMobile = this.mobileQuery.matches;
      if (!this.isMobile) {
        this.closeMobile();
      }
    },
    toggleMobile() {
      this.mobileOpen ? this.closeMobile() : this.openMobile();
    },
    openMobile() {
      this.mobileOpen = true;
      document.body.classList.add('ew-sidebar-locked');
    },
    closeMobile() {
      this.mobileOpen = false;
      document.body.classList.remove('ew-sidebar-locked');
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.mobileOpen) {
        this.closeMobile();
      }
    },
    formatCurrency(value) {
      return Number(value).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
    },
    getTotalAmountPrepaids() {
      this.total_monedero = 0;
      this.isLoadingBalance = true;
      axios
        .get(`${commons.apiURL}/api/v1/prepaid/getTotalAmount/${localStorage.getItem("ew-user-id")}`)
        .then((response) => {
          this.total_monedero = parseFloat(response.data.total_amount);
          if (!this.hasRole(['helpdesk']) && this.total_monedero <= 10000) {
            this.showLowBalanceAlert = true;
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isLoadingBalance = false;
        });
    }
  }
};
</script>

<style>
body.ew-sidebar-locked {
  overflow: hidden;
}
</style>

<style scoped>
.sidebar {
  --navy: #031d7d;
  --navy-dark: #021563;
  --magenta: #a80b5f;
  --magenta-dark: #8a0a4f;

  width: 240px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(180deg, var(--navy) 0%, var(--navy-dark) 100%);
  box-shadow: 2px 0 12px rgba(3, 29, 125, 0.12);
  transition: width 0.25s ease;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
}

.sidebar.collapsed {
  width: 68px;
}

/* Header (solo móvil) */
.sidebar-mobile-header {
  padding: 12px 12px 0;
}

.sidebar-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  border-radius: 8px !important;
  color: #fff;
  font-size: 0.9rem;
  transition: background-color 0.15s ease;
}

.sidebar-icon-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

/* Card saldo */
.saldo-card {
  flex-shrink: 0;
  margin: 10px 12px 4px;
  padding: 10px 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--magenta) 0%, var(--magenta-dark) 100%);
  color: #fff;
}

.saldo-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.9;
}

.saldo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}

.saldo-amount {
  font-size: 1.1rem;
  font-weight: 700;
  white-space: nowrap;
}

.saldo-amount small {
  font-size: 0.7rem;
  font-weight: 600;
  opacity: 0.85;
}

.saldo-link {
  display: inline-flex;
  color: #fff;
  font-size: 1.15rem;
  opacity: 0.85;
  transition: opacity 0.15s ease;
}

.saldo-link:hover {
  color: #fff;
  opacity: 1;
}

.ew-balance-skeleton {
  height: 1.3rem;
  width: 65%;
  margin: 2px 0;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.25) 25%, rgba(255, 255, 255, 0.45) 37%, rgba(255, 255, 255, 0.25) 63%);
  background-size: 400% 100%;
  animation: ew-balance-shimmer 1.4s ease infinite;
}

@keyframes ew-balance-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* Menú */
.sidebar-menu {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 10px 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
}

.sidebar.collapsed .sidebar-menu {
  padding-top: 10px;
}

.sidebar-section + .sidebar-section {
  margin-top: 2px;
}

.sidebar-section-label {
  padding: 6px 10px 3px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sidebar.collapsed .sidebar-section-label {
  height: 1px;
  margin: 10px 8px;
  padding: 0;
  background: rgba(255, 255, 255, 0.18);
}

.sidebar-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 1px 0;
  padding: 7px 10px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.9rem;
  line-height: 20px;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.sidebar.collapsed .sidebar-link {
  justify-content: center;
  padding: 8px 0;
}

.sidebar-link:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  text-decoration: none;
}

.sidebar-link:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.7);
  outline-offset: -2px;
}

.sidebar-link-icon {
  flex-shrink: 0;
  width: 20px;
  font-size: 1.1rem;
  line-height: 1;
  text-align: center;
}

.sidebar-link-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footer: botón de colapsar */
.sidebar-footer {
  flex-shrink: 0;
  padding: 6px 10px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.sidebar-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 6px 0;
  background: none;
  border: none;
  border-radius: 8px !important;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.sidebar-collapse-btn i {
  width: 20px;
  text-align: center;
}

.sidebar-collapse-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* Activo: navy aclarado + barra magenta; el magenta sólido queda para el saldo */
.active-link,
.active-link:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-weight: 600;
}

.active-link::before {
  content: '';
  position: absolute;
  top: 7px;
  bottom: 7px;
  left: 0;
  width: 3px;
  border-radius: 0 3px 3px 0;
  /* magenta de marca aclarado para que se distinga sobre el navy */
  background: #d6338a;
}

.sidebar-tip {
  position: fixed;
  z-index: 1060;
  transform: translateY(-50%);
  padding: 6px 10px;
  border-radius: 6px;
  background: #1f2333;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sidebar-tip::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -4px;
  width: 8px;
  height: 8px;
  background: #1f2333;
  transform: translateY(-50%) rotate(45deg);
}

/* Móvil y tablet: el menú se abre como cajón lateral */
.sidebar-backdrop {
  display: none;
}

@media (max-width: 991.98px) {
  .sidebar,
  .sidebar.collapsed {
    position: fixed;
    top: 0;
    left: 0;
    width: 264px;
    z-index: 1050;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }

  .sidebar.open {
    transform: translateX(0);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.25);
  }


  .sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1049;
    background: rgba(3, 13, 50, 0.45);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar,
  .sidebar-link,
  .sidebar-icon-btn,
  .sidebar-collapse-btn {
    transition: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Sidebar.vue